import { useEffect, useState, type KeyboardEvent, type PointerEvent } from "react";
import { Reorder, useDragControls, type DragControls } from "framer-motion";
import {
  BookCheck,
  GripVertical,
  Megaphone,
  MessageCircleCode,
  RotateCcw,
  Search,
  Settings,
  UsersIcon,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Frame, FramePanel } from "@/components/ui/frame";
import { cn } from "@/lib/utils";
import {
  DEFAULT_SIDEBAR_PREFS,
  MAX_ITEMS_SHOWN,
  isGroupKey,
  prefsAreEqual,
  useSidebarPrefsStore,
  type SidebarGroupPrefs,
  type SidebarMenuKey,
  type SidebarPrefs,
} from "@/stores/sidebar/sidebarPrefsStore";
import NumberFlow from "@number-flow/react";

/**
 * "Customize sidebar" — opened by the gear that appears next to "Main Menu"
 * on sidebar hover (app-sidebar.tsx). One list mirrors the Main Menu: every
 * row drags to reorder, and the three collapsible groups (Campaigns, Ask Amy,
 * Settings) also carry their "open by default" switch and an "items shown"
 * slider. Edits are staged and only persist on Save (localStorage, this
 * device only); Cancel / closing discards them. Reset to defaults is staged
 * too, so it still needs a Save.
 */

// Labels + icons mirror the rows app-sidebar.tsx renders for each key.
const MENU_ITEMS: Record<SidebarMenuKey, { label: string; icon: LucideIcon }> = {
  search: { label: "Search", icon: Search },
  campaigns: { label: "Campaigns", icon: Megaphone },
  amy: { label: "Ask Amy", icon: MessageCircleCode },
  crm: { label: "Global CRM", icon: UsersIcon },
  tasks: { label: "Tasks", icon: BookCheck },
  settings: { label: "Settings", icon: Settings },
};

function moveKey(order: SidebarMenuKey[], key: SidebarMenuKey, delta: -1 | 1) {
  const from = order.indexOf(key);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= order.length) return order;
  const next = [...order];
  next.splice(from, 1);
  next.splice(to, 0, key);
  return next;
}

/** The grip: only it starts a drag; arrow keys on it move the row by one. */
function DragHandle({
  label,
  controls,
  onMove,
}: {
  label: string;
  controls: DragControls;
  onMove: (delta: -1 | 1) => void;
}) {
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      onMove(-1);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      onMove(1);
    }
  };

  return (
    <button
      type="button"
      aria-label={`Drag to reorder ${label}`}
      onPointerDown={(e: PointerEvent<HTMLButtonElement>) => controls.start(e)}
      onKeyDown={onKeyDown}
      className="-ml-1 flex size-6 shrink-0 cursor-grab touch-none items-center justify-center rounded-sm text-muted-foreground/60 hover:bg-muted hover:text-foreground active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <GripVertical className="size-4" />
    </button>
  );
}

/**
 * One Main Menu row. Plain entries are grip + icon + label. Collapsible
 * groups add the "open by default" switch on the right (the text acts as its
 * label) and an "items shown" slider nested underneath.
 */
function MenuRow({
  itemKey,
  groupPrefs,
  onMove,
  onGroupChange,
}: {
  itemKey: SidebarMenuKey;
  /** Present only for the collapsible groups. */
  groupPrefs?: SidebarGroupPrefs;
  onMove: (delta: -1 | 1) => void;
  onGroupChange: (next: SidebarGroupPrefs) => void;
}) {
  const controls = useDragControls();
  const [dragging, setDragging] = useState(false);
  const { label, icon: Icon } = MENU_ITEMS[itemKey];
  const neverExpands = groupPrefs?.itemsShown === 0;
  const switchId = `sidebar-group-open-${itemKey}`;

  return (
    <Reorder.Item
      value={itemKey}
      dragListener={false}
      dragControls={controls}
      onDragStart={() => setDragging(true)}
      onDragEnd={() => setDragging(false)}
      className={cn(
        "relative flex flex-col select-none dark:bg-background bg-sidebar",
        dragging && "z-10 scale-90"
      )}
    >
      <div className="flex items-center gap-2.5 px-4 py-3 text-sm">
        <DragHandle label={label} controls={controls} onMove={onMove} />

        {groupPrefs ? (
          <label
            htmlFor={switchId}
            className={cn(
              "flex min-w-0 flex-1 items-center gap-2.5",
              neverExpands ? "cursor-default" : "cursor-pointer"
            )}
          >
            <Icon className="size-4 shrink-0 text-muted-foreground" />
            <span className="flex min-w-0 flex-col">
              <span className="truncate font-medium">{label}</span>
            </span>
          </label>
        ) : (
          <>
            <Icon className="size-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{label}</span>
          </>
        )}

        {groupPrefs && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {neverExpands ? "Never expands" : "Open by default"}
            </span>
            <Switch
              id={switchId}
              size="xs"
              checked={!neverExpands && groupPrefs.defaultOpen}
              disabled={neverExpands}
              onCheckedChange={(value) =>
                onGroupChange({ ...groupPrefs, defaultOpen: value })
              }
            />
          </div>
        )}
      </div>

      {groupPrefs && (
        // Dashed rail sits under the icon's centre; the text aligns with the label.
        <div className="mb-2.5 ml-12.5 mr-3 flex items-center gap-3 border-l border-dashed border-muted-foreground/20 py-1 pl-4.5">
          <span className="w-20 shrink-0 text-xs text-muted-foreground">
            Items shown
          </span>
          <Slider
            aria-label={`${label} items shown`}
            min={0}
            max={MAX_ITEMS_SHOWN}
            step={1}
            value={[groupPrefs.itemsShown]}
            onValueChange={([v]) => onGroupChange({ ...groupPrefs, itemsShown: v })}
            className="flex-1"
          />
          <span className="w-6 shrink-0 text-right font-mono text-xs tabular-nums">
            <NumberFlow value={groupPrefs.itemsShown} />
          </span>
        </div>
      )}
    </Reorder.Item>
  );
}

export function SidebarSettingsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const prefs = useSidebarPrefsStore((s) => s.prefs);
  const savePrefs = useSidebarPrefsStore((s) => s.savePrefs);
  const [staged, setStaged] = useState<SidebarPrefs>(prefs);

  // Re-seed from the saved prefs every time the dialog opens.
  useEffect(() => {
    if (open) setStaged(prefs);
  }, [open, prefs]);

  const dirty = !prefsAreEqual(staged, prefs);
  const isDefault = prefsAreEqual(staged, DEFAULT_SIDEBAR_PREFS);

  const setOrder = (order: SidebarMenuKey[]) =>
    setStaged((prev) => ({ ...prev, order }));

  const handleSave = () => {
    savePrefs(staged);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Customize sidebar</DialogTitle>
          <DialogDescription>Drag to reorder. Saved on this device.</DialogDescription>
        </DialogHeader>

        <Frame>
          <FramePanel className="p-0">
            <Reorder.Group
              axis="y"
              values={staged.order}
              onReorder={setOrder}
              className="flex flex-col divide-y divide-border"
            >
              {staged.order.map((key) => {
                const groupKey = isGroupKey(key) ? key : null;
                return (
                  <MenuRow
                    key={key}
                    itemKey={key}
                    groupPrefs={groupKey ? staged.groups[groupKey] : undefined}
                    onMove={(delta) => setOrder(moveKey(staged.order, key, delta))}
                    onGroupChange={(next) => {
                      if (!groupKey) return;
                      setStaged((prev) => ({
                        ...prev,
                        groups: { ...prev.groups, [groupKey]: next },
                      }));
                    }}
                  />
                );
              })}
            </Reorder.Group>
          </FramePanel>
        </Frame>

        <DialogFooter className="sm:justify-between">
          <Button
            variant="muted-ghost"
            size="sm"
           
            disabled={isDefault}
            onClick={() => setStaged(DEFAULT_SIDEBAR_PREFS)}
          >
            <RotateCcw />
            Reset to defaults
          </Button>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!dirty}>
              Save Layout
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
